// src/admin/pages/DataroomTemplatesPage.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Folder, 
  Eye
} from 'lucide-react';

const DataroomTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    folders: [],
    newFolder: ''
  });

  // Mock templates - in real implementation, these would come from an API
  const mockTemplates = [
    {
      id: 1,
      name: "Performance Reporting",
      description: "Template for quarterly and annual performance reporting with financial statements, KPIs, and investor updates",
      folders: [
        "Q1 Reports",
        "Q2 Reports", 
        "Q3 Reports",
        "Q4 Reports",
        "Annual Reports",
        "Financial Statements",
        "KPI Dashboards",
        "Investor Updates"
      ],
      created_at: "2024-01-15T10:00:00Z",
      usage_count: 12,
      is_system: true
    },
    {
      id: 2,
      name: "Due Diligence",
      description: "Comprehensive due diligence template with legal, financial, and operational documentation",
      folders: [
        "Legal Documents",
        "Financial Records",
        "Operational Reports",
        "HR Documentation",
        "IP & Patents",
        "Contracts & Agreements",
        "Insurance Policies",
        "Compliance Documents"
      ],
      created_at: "2024-01-10T14:30:00Z",
      is_system: true
    },
    {
      id: 3,
      name: "Capital Raising",
      description: "Template for capital raising campaigns with investor materials and pitch decks",
      folders: [
        "Pitch Decks",
        "Financial Models",
        "Business Plans",
        "Market Research",
        "Due Diligence",
        "Legal Documents",
        "Investor Updates",
        "Term Sheets"
      ],
      created_at: "2024-01-05T09:15:00Z",
      is_system: true
    },
    {
      id: 4,
      name: "M&A Process",
      description: "Template for mergers and acquisitions with vendor due diligence and integration planning",
      folders: [
        "Vendor Due Diligence",
        "Financial Analysis",
        "Legal Review",
        "Integration Planning",
        "Synergy Analysis",
        "Risk Assessment",
        "Communication Plans",
        "Post-Merger Reports"
      ],
      created_at: "2024-01-01T16:45:00Z",
      is_system: true
    },
    {
      id: 5,
      name: "IPO Preparation",
      description: "Template for initial public offering preparation with regulatory and investor documentation",
      folders: [
        "Regulatory Filings",
        "Financial Statements",
        "Prospectus Materials",
        "Investor Presentations",
        "Legal Documentation",
        "Audit Reports",
        "Risk Factors",
        "Market Analysis"
      ],
      created_at: "2023-12-20T11:20:00Z",
      is_system: true
    },
    {
      id: 6,
      name: "ESG Reporting",
      description: "Environmental, Social, and Governance reporting template with sustainability metrics",
      folders: [
        "Environmental Reports",
        "Social Impact",
        "Governance Policies",
        "Sustainability Metrics",
        "Carbon Footprint",
        "Diversity & Inclusion",
        "Ethics & Compliance",
        "Stakeholder Engagement"
      ],
      created_at: "2023-12-15T13:10:00Z",
      is_system: true
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setTemplates(mockTemplates);
      setLoading(false);
    }, 500);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateTemplate = () => {
    const template = {
      id: Date.now(),
      name: newTemplate.name,
      description: newTemplate.description,
      folders: [...newTemplate.folders],
      created_at: new Date().toISOString(),
      is_system: true
    };
    
    setTemplates([...templates, template]);
    setNewTemplate({
      name: '',
      description: '',
      folders: [],
      newFolder: ''
    });
    setShowCreateModal(false);
  };

  const handleUpdateTemplate = (updatedData) => {
    if (!editingTemplate) return;
    
    setTemplates(templates.map(template => 
      template.id === editingTemplate.id ? { ...template, ...updatedData } : template
    ));
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = (id) => {
    if (!window.confirm('Are you sure you want to delete this template? This will affect all users.')) {
      return;
    }
    setTemplates(templates.filter(template => template.id !== id));
  };

  const addFolder = () => {
    if (newTemplate.newFolder.trim()) {
      setNewTemplate({
        ...newTemplate,
        folders: [...newTemplate.folders, newTemplate.newFolder.trim()],
        newFolder: ''
      });
    }
  };

  const removeFolder = (index) => {
    setNewTemplate({
      ...newTemplate,
      folders: newTemplate.folders.filter((_, i) => i !== index)
    });
  };


  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dealroom Templates</h2>
          <p className="text-gray-600 mt-1">Manage dealroom templates and folder structures for all users</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <Folder className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mt-2 mb-4">{template.description}</p>
              
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Folders ({template.folders.length})</p>
                <div className="flex flex-wrap gap-1">
                  {template.folders.slice(0, 3).map((folder, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                      {folder}
                    </span>
                  ))}
                  {template.folders.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                      +{template.folders.length - 3} more
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>{new Date(template.created_at).toLocaleDateString()}</span>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowPreview(template)}
                  className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </button>
                <button
                  onClick={() => setEditingTemplate(template)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  className="flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>


      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Template</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); handleCreateTemplate(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template Name *</label>
                    <input
                      type="text"
                      required
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={3}
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Folders</label>
                    <div className="mt-2 space-y-2">
                      {newTemplate.folders.map((folder, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{folder}</span>
                          <button
                            type="button"
                            onClick={() => removeFolder(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newTemplate.newFolder}
                          onChange={(e) => setNewTemplate({...newTemplate, newFolder: e.target.value})}
                          placeholder="Add folder name"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addFolder();
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={addFolder}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Create Template
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Template</h3>
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={(e) => { 
                e.preventDefault(); 
                handleUpdateTemplate({
                  name: editingTemplate.name,
                  description: editingTemplate.description,
                  folders: editingTemplate.folders
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template Name *</label>
                    <input
                      type="text"
                      required
                      value={editingTemplate.name}
                      onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={3}
                      value={editingTemplate.description}
                      onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Folders</label>
                    <div className="mt-2 space-y-2">
                      {editingTemplate.folders.map((folder, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{folder}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newFolders = editingTemplate.folders.filter((_, i) => i !== index);
                              setEditingTemplate({...editingTemplate, folders: newFolders});
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          id="editNewFolder"
                          placeholder="Add folder name"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const folderName = e.target.value.trim();
                              if (folderName) {
                                setEditingTemplate({
                                  ...editingTemplate,
                                  folders: [...editingTemplate.folders, folderName]
                                });
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.getElementById('editNewFolder');
                            const folderName = input.value.trim();
                            if (folderName) {
                              setEditingTemplate({
                                ...editingTemplate,
                                folders: [...editingTemplate.folders, folderName]
                              });
                              input.value = '';
                            }
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setEditingTemplate(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Update Template
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Template Preview: {showPreview.name}</h3>
                <button
                  onClick={() => setShowPreview(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Description</h4>
                  <p className="text-sm text-gray-600">{showPreview.description}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Folder Structure</h4>
                  <div className="mt-2 space-y-1">
                    {showPreview.folders.map((folder, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <Folder className="h-4 w-4 mr-2 text-gray-400" />
                        {folder}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
                  <span>Created {new Date(showPreview.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataroomTemplatesPage;
